// FacetPopover — generic checkbox-list popover used by the Locations,
// Actions, and Actors filter buttons in the v5 audit-log header.
//
// Lifecycle:
//   - Parent owns `open`, `selected`, and `onApply`. The popover does its
//     own search-filtering and click-outside dismissal.
//   - "Apply" calls onApply(next) with the new selection; "Clear" applies
//     an empty selection. The popover does NOT close itself; the parent
//     decides when to dismiss (matches the mockup, which shows the popover
//     re-open with the cleared state).

import { useEffect, useMemo, useRef, useState } from "react";
import "./Audit.css";

export interface FacetOption {
  /** Stable key persisted to URL params. */
  value: string;
  /** Visible label. */
  label: string;
  /** Mono-style label (e.g. site-IAD12). */
  mono?: boolean;
  /** Optional badge count for the right side. Hidden when undefined. */
  count?: number;
  /** Optional group key (e.g. category name). When any option in the
   *  popover has a group, the popover renders a 2-level tree with
   *  collapsible group headers and a parent-checkbox that toggles all
   *  children at once (PRD §1.11). */
  group?: string;
}

export interface FacetPopoverProps {
  open: boolean;
  /** Header label rendered inside the popover. */
  searchPlaceholder: string;
  options: FacetOption[];
  selected: string[];
  onApply: (selected: string[]) => void;
  onClose: () => void;
  /** Optional anchor className for positioning (the parent renders this
   *  inside a `position: relative` container; the popover is absolute). */
  anchorStyle?: React.CSSProperties;
  /** Called every time the user toggles a row, so the parent can persist
   *  the selection without an explicit Apply if it wants. The mockup uses
   *  Apply but keeping this hook makes the component reusable. */
  onChange?: (selected: string[]) => void;
}

export function FacetPopover(props: FacetPopoverProps): JSX.Element | null {
  const {
    open,
    searchPlaceholder,
    options,
    selected,
    onApply,
    onClose,
    anchorStyle,
    onChange,
  } = props;
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<string[]>(selected);
  const popRef = useRef<HTMLDivElement>(null);

  // Reset draft whenever the popover opens; this matches the mockup which
  // uses an explicit Apply button to commit. Keep the draft separate so
  // the user can preview a change without committing.
  useEffect(() => {
    if (open) {
      setDraft(selected);
      setSearch("");
    }
  }, [open, selected]);

  // Click-outside-to-close. attached only when open to keep the listener
  // count low.
  useEffect(() => {
    if (!open) return;
    function handler(ev: MouseEvent) {
      if (!popRef.current) return;
      if (popRef.current.contains(ev.target as Node)) return;
      // Defer one tick so the toggle button's own click doesn't immediately
      // re-open the popover on the same event loop.
      onClose();
    }
    // mousedown not click — closes before the button's click handler fires,
    // which is the right behaviour for a toggle anchor.
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q === "") return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q),
    );
  }, [options, search]);

  // Group structure — only kicks in when at least one option carries a group.
  const groups = useMemo(() => {
    const useGroups = filtered.some((o) => o.group !== undefined);
    if (!useGroups) return null;
    const map = new Map<string, FacetOption[]>();
    for (const o of filtered) {
      const key = o.group ?? "Other";
      const arr = map.get(key);
      if (arr) {
        arr.push(o);
      } else {
        map.set(key, [o]);
      }
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  // Default groups to expanded; collapsing is per-popover-open transient state.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (open) setCollapsed(new Set());
  }, [open]);

  if (!open) return null;

  function toggle(value: string) {
    setDraft((cur) => {
      const next = cur.includes(value)
        ? cur.filter((v) => v !== value)
        : [...cur, value];
      onChange?.(next);
      return next;
    });
  }

  function toggleGroup(group: string, members: FacetOption[]) {
    setDraft((cur) => {
      const memberValues = members.map((m) => m.value);
      const allSelected = memberValues.every((v) => cur.includes(v));
      const next = allSelected
        ? cur.filter((v) => !memberValues.includes(v))
        : Array.from(new Set([...cur, ...memberValues]));
      onChange?.(next);
      return next;
    });
    // Expand the group on parent-toggle for visual feedback.
    setCollapsed((s) => {
      const ns = new Set(s);
      ns.delete(group);
      return ns;
    });
  }

  function toggleCollapsed(group: string) {
    setCollapsed((s) => {
      const ns = new Set(s);
      if (ns.has(group)) ns.delete(group);
      else ns.add(group);
      return ns;
    });
  }

  return (
    <div
      ref={popRef}
      className="rb-audit__popover"
      style={anchorStyle}
      role="dialog"
      aria-label={searchPlaceholder}
    >
      <div className="rb-audit__popover-search">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          autoFocus
        />
      </div>
      <div className="rb-audit__popover-list">
        {filtered.length === 0 ? (
          <div className="rb-audit__popover-empty">No matches.</div>
        ) : groups !== null ? (
          // Grouped — render each group header (parent toggle) + children.
          groups.map(([groupName, members]) => {
            const memberValues = members.map((m) => m.value);
            const allSelected = memberValues.every((v) => draft.includes(v));
            const someSelected = memberValues.some((v) => draft.includes(v));
            const isCollapsed = collapsed.has(groupName);
            const groupCount = members.reduce(
              (sum, m) => sum + (m.count ?? 0),
              0,
            );
            return (
              <div key={groupName} className="rb-audit__popover-group">
                <div className="rb-audit__popover-grouphead">
                  <button
                    type="button"
                    className={
                      allSelected
                        ? "rb-audit__popover-row rb-audit__popover-row--checked rb-audit__popover-row--parent"
                        : someSelected
                          ? "rb-audit__popover-row rb-audit__popover-row--mixed rb-audit__popover-row--parent"
                          : "rb-audit__popover-row rb-audit__popover-row--parent"
                    }
                    onClick={() => toggleGroup(groupName, members)}
                    aria-pressed={allSelected}
                  >
                    <span className="rb-audit__popover-chk" aria-hidden />
                    <span className="rb-audit__popover-name">{groupName}</span>
                    {groupCount > 0 && (
                      <span className="rb-audit__popover-count">{groupCount}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    className="rb-audit__popover-collapse"
                    onClick={() => toggleCollapsed(groupName)}
                    aria-label={isCollapsed ? "Expand" : "Collapse"}
                  >
                    {isCollapsed ? "▸" : "▾"}
                  </button>
                </div>
                {!isCollapsed &&
                  members.map((opt) => {
                    const checked = draft.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={
                          checked
                            ? "rb-audit__popover-row rb-audit__popover-row--checked rb-audit__popover-row--child"
                            : "rb-audit__popover-row rb-audit__popover-row--child"
                        }
                        onClick={() => toggle(opt.value)}
                        aria-pressed={checked}
                      >
                        <span className="rb-audit__popover-chk" aria-hidden />
                        <span
                          className={
                            opt.mono
                              ? "rb-audit__popover-name rb-audit__popover-name--mono"
                              : "rb-audit__popover-name"
                          }
                        >
                          {opt.label}
                        </span>
                        {opt.count !== undefined && (
                          <span className="rb-audit__popover-count">{opt.count}</span>
                        )}
                      </button>
                    );
                  })}
              </div>
            );
          })
        ) : (
          filtered.map((opt) => {
            const checked = draft.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                className={
                  checked
                    ? "rb-audit__popover-row rb-audit__popover-row--checked"
                    : "rb-audit__popover-row"
                }
                onClick={() => toggle(opt.value)}
                aria-pressed={checked}
              >
                <span className="rb-audit__popover-chk" aria-hidden />
                <span
                  className={
                    opt.mono
                      ? "rb-audit__popover-name rb-audit__popover-name--mono"
                      : "rb-audit__popover-name"
                  }
                >
                  {opt.label}
                </span>
                {opt.count !== undefined && (
                  <span className="rb-audit__popover-count">{opt.count}</span>
                )}
              </button>
            );
          })
        )}
      </div>
      <div className="rb-audit__popover-footer">
        <button
          type="button"
          onClick={() => {
            setDraft([]);
            onApply([]);
          }}
          disabled={draft.length === 0 && selected.length === 0}
        >
          Clear
        </button>
        <button
          type="button"
          onClick={() => onApply(draft)}
        >
          Apply{draft.length > 0 ? ` (${draft.length})` : ""}
        </button>
      </div>
    </div>
  );
}
